import { ChangeEvent, FormEvent } from 'react';
import { VALIDITY } from '@/constants/validity';
import { Button } from '@/ui-components/atoms/Button/Button';
import { Input } from '@/ui-components/atoms/Inputs/Input/Input';
import { Link } from '@/ui-components/atoms/Link/Link';
import { Text } from '@/ui-components/atoms/Text/Text';
import {
    AppDownloadButtons,
    DownloadButtonProps,
} from '@/ui-components/molecules/AppDownloadButtons/AppDownloadButtons';
import styles from './snapshotpopover.module.scss';

const REGISTER_URL = 'https://care.siriusxm.com/account/registration';

interface SnapshotUserAnonymousSignInProps {
    appdownloadItems: DownloadButtonProps[];
    hasError: boolean;
    identifier: string;
    invalidMessage?: string;
    onIdentifierChange: (event: ChangeEvent<HTMLInputElement>) => void;
    onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}

export function SnapshotUserAnonymousSignIn({
    appdownloadItems,
    hasError,
    identifier,
    invalidMessage,
    onIdentifierChange,
    onSubmit,
}: SnapshotUserAnonymousSignInProps): React.JSX.Element {
    return (
        <form className={styles.snapshotUserForm} onSubmit={onSubmit} noValidate>
            <div className={styles.snapshotAnonymousMain}>
                <div className={styles.snapshotAnonymousSection}>
                    <Text tag="h3" className={styles.snapshotAnonymousTitle}>
                        Sign in
                    </Text>

                    {hasError && (
                        <div className={styles.snapshotErrorSummary}>
                            <Text tag="p" className={styles.snapshotErrorSummaryText}>
                                Please fix the error in the form below.
                            </Text>
                        </div>
                    )}

                    <Input
                        ariaLabel="Email or username"
                        inputWrapperStyles={styles.snapshotInputWrapper}
                        invalid={hasError ? VALIDITY.invalid : VALIDITY.untouched}
                        invalidMessage={invalidMessage}
                        label="Email or username"
                        name="snapshot-identifier"
                        noSpacing={true}
                        onChange={onIdentifierChange}
                        type="text"
                        value={identifier}
                    />
                </div>

                <div className={styles.snapshotAnonymousActions}>
                    <div className={styles.snapshotAnonymousButtonGroup}>
                        <div className={styles.snapshotAnonymousPrimaryActions}>
                            <Button
                                ariaLabel="Continue"
                                fitting="fill-container"
                                size="small"
                                type="submit"
                                variant="primary"
                            >
                                Continue
                            </Button>
                        </div>

                        <button className={styles.inlineActionButton} type="button">
                            Forgot your login?
                        </button>
                    </div>

                    <Text tag="p" className={styles.snapshotCopy}>
                        No account?{' '}
                        <Link className={styles.inlineLink} href={REGISTER_URL}>
                            Register
                        </Link>
                    </Text>
                </div>
            </div>

            <div className={styles.snapshotDivider}>
                <div className={styles.snapshotDividerLine} />
            </div>

            <div className={styles.snapshotAnonymousFooter}>
                <Text tag="h4" className={styles.snapshotAppHeading}>
                    Get the SiriusXM app
                </Text>
                <AppDownloadButtons
                    items={appdownloadItems}
                    className={styles.snapshotDownloadButtons}
                    appleClassName={styles.snapshotStoreBadge}
                    androidClassName={styles.snapshotStoreBadge}
                />
            </div>
        </form>
    );
}
